import { Link } from 'react-router';
import { Badge, EmptyState, InlineError, Panel, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { formatDate, statusTone } from '../listView/TaskTable';
import { TASK_PRIORITY_LABELS } from '../tasks/types';
import { useMyTasks } from './hooks';
import { DUE_BUCKET_LABELS, groupByDue, localIsoDate, myTaskHome, myTaskHref } from './types';
import type { MyTask } from './types';

/**
 * My Tasks: everything assigned to the signed-in person, across every
 * project, arranged by urgency. Each row links into its project, where the
 * full task panel lives.
 */

function TaskRow({ task, overdue }: { task: MyTask; overdue: boolean }) {
  const href = myTaskHref(task);
  return (
    <li className="my-task-row">
      <div className="my-task-main">
        {href === null ? (
          <span className="my-task-title">{task.title}</span>
        ) : (
          <Link to={href} className="my-task-title">
            {task.title}
          </Link>
        )}
        <span className="my-task-project">
          {myTaskHome(task)}
          {task.parent_task_id === null ? '' : ' · subtask'}
        </span>
      </div>
      <span className="my-task-facts">
        {task.project_statuses ? (
          <Badge tone={statusTone(task.project_statuses.color_key)}>
            {task.project_statuses.name}
          </Badge>
        ) : (
          <Badge tone="neutral">Uncategorized</Badge>
        )}
        {task.priority === null ? null : (
          <span className="my-task-priority">{TASK_PRIORITY_LABELS[task.priority]}</span>
        )}
        <span className={overdue ? 'my-task-due my-task-due--overdue' : 'my-task-due'}>
          {task.due_date === null ? 'No due date' : `Due ${formatDate(task.due_date)}`}
        </span>
      </span>
    </li>
  );
}

export function MyTasksPage() {
  const tasks = useMyTasks();
  const buckets = groupByDue(tasks.data ?? [], localIsoDate(new Date()));
  const openCount = buckets.reduce((sum, bucket) => sum + bucket.tasks.length, 0);

  return (
    <>
      {/* The description stays: "across every project" answers the question
          people actually have about this page, and the title does not. */}
      <PageHeader
        title="My Tasks"
        topbar
        description="Everything assigned to you, across every project you can see."
      />
      <Panel title={openCount === 1 ? '1 open task' : `${openCount} open tasks`}>
        {tasks.isPending ? (
          <Skeleton lines={4} label="Loading your tasks" />
        ) : tasks.isError ? (
          <InlineError>Your tasks could not be loaded. Refresh to try again.</InlineError>
        ) : openCount === 0 ? (
          <EmptyState
            title="Nothing assigned to you"
            description="When a task is assigned to you in any project, it appears here."
          />
        ) : (
          buckets.map((bucket) => (
            <section key={bucket.key} aria-label={DUE_BUCKET_LABELS[bucket.key]}>
              <h3 className="my-task-bucket">
                {DUE_BUCKET_LABELS[bucket.key]} ({bucket.tasks.length})
              </h3>
              <ul className="my-task-list">
                {bucket.tasks.map((task) => (
                  <TaskRow key={task.id} task={task} overdue={bucket.key === 'overdue'} />
                ))}
              </ul>
            </section>
          ))
        )}
      </Panel>
    </>
  );
}
